import {useEffect, useRef, useState} from 'react';

export interface BadgeArtifactHtmlState {
  html: string | undefined;
  isLoading: boolean;
  error: string | undefined;
}

// Fetches the self-contained HTML artifact from a badge's presigned S3
// download URL (`PersonnelBadgeDetailResponse.downloadUrl`). Presigned URLs
// carry their own signed auth in the query string — no app auth headers are
// attached, matching how other presigned S3 links are consumed elsewhere in
// the app (e.g. `imageFileFromUrl` in `src/shared/utils/imageHandler.ts`,
// and personnel avatar `<img src>` usage), so a plain `fetch` is correct
// here.
export function useBadgeArtifactHtml(
  downloadUrl: string | undefined
): BadgeArtifactHtmlState {
  const [html, setHtml] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!downloadUrl) {
      setHtml(undefined);
      setError(undefined);
      return;
    }

    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    setError(undefined);

    fetch(downloadUrl)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to load badge artifact (${response.status})`);
        }
        return response.text();
      })
      .then(text => {
        if (requestIdRef.current === requestId) {
          setHtml(text);
        }
      })
      .catch((err: unknown) => {
        if (requestIdRef.current === requestId) {
          setHtml(undefined);
          setError(
            err instanceof Error ? err.message : 'Failed to load badge artifact.'
          );
        }
      })
      .finally(() => {
        if (requestIdRef.current === requestId) {
          setIsLoading(false);
        }
      });
  }, [downloadUrl]);

  return {html, isLoading, error};
}
